import { isEqual } from "lodash";
import Icon from "@/shared/ui/Icon";
import CreateCharacterBtn from "@/domains/createCharacter/components/CreateCharacterBtn";

type ValueTitleFn<T> = (value: T) => string | number;
type ValueTitleKey<T> = T extends object ? keyof T : never;

interface ArrowSwitcherProps<T> {
  variants: T[];
  className?: string;
  /**
   * Либо функция, которая возвращает строку/число для отображения,
   * либо ключ объекта (если T — объект), по которому получаем отображаемое значение.
   */
  valueTitle?: ValueTitleFn<T> | ValueTitleKey<T>;
  onChange?: (value: T) => void;
  /**
   * Если type === "color", компонент будет рендерить набор цветных кнопок.
   * Иначе — обычный переключатель со стрелками и текстом.
   */
  type: string;
  /** Текущее значение (один из вариантов). */
  value: T;
  valueDefault?: T;
}

function ArrowSwitcher<T>({
  variants,
  className,
  valueTitle,
  valueDefault,
  onChange,
  type,
  value,
}: ArrowSwitcherProps<T>) {
  let title: string | number = "";

  // Определяем, что будет показано как "заголовок" (title)
  if (value != null) {
    if (typeof valueTitle === "function") {
      // Если valueTitle — функция, вызываем её
      title = valueTitle(value);
    } else if (typeof valueTitle === "string" && typeof value === "object" && value !== null) {
      // Если valueTitle — строка и value — объект, берём соответствующее поле
      const obj = value as Record<string, unknown>;
      const val = obj[valueTitle];
      title = typeof val === "string" || typeof val === "number" ? val : "";
    } else {
      // Иначе считаем, что value сам по себе — строка/число
      title = value as string | number;
    }
  }

  if (valueDefault && isEqual(valueDefault, value)) {
    title = "Нет";
  }

  const handlePrev = () => {
    let currentIndex = variants.findIndex((item) => item === value);
    if (currentIndex === -1) {
      currentIndex = 0;
    }
    const prevIndex = currentIndex === 0 ? variants.length - 1 : currentIndex - 1;
    const newValue = variants[prevIndex];
    onChange?.(newValue);
  };

  const handleNext = () => {
    let currentIndex = variants.findIndex((item) => item === value);
    if (currentIndex === -1) {
      currentIndex = 0;
    }
    const nextIndex = currentIndex === variants.length - 1 ? 0 : currentIndex + 1;
    const newValue = variants[nextIndex];
    onChange?.(newValue);
  };

  return (
    <div
      className={`flex gap-1.5 w-50 shrink-0 justify-between items-center text-15 font-bold ${
        className || ""
      }`}
    >
      <CreateCharacterBtn onClick={handlePrev}>
        <Icon name="triangle-right" className="w-2.5 -scale-x-100" />
      </CreateCharacterBtn>
      {!type && <span className="whitespace-nowrap">{title}</span>}
      {type === "color" && (
        <div className="flex gap-0.5">
          {variants.map((color) => (
            <button
              type="button"
              key={String(color)}
              className="w-5 h-5 rounded border bg-gray-100 aria-pressed:border-2 aria-pressed:p-px bg-clip-content aria-pressed:border-white"
              style={{ backgroundColor: String(color) }}
              aria-pressed={value === color}
              onClick={() => onChange?.(color)}
            />
          ))}
        </div>
      )}

      <CreateCharacterBtn onClick={handleNext}>
        <Icon name="triangle-right" className="w-2.5" />
      </CreateCharacterBtn>
    </div>
  );
}

export default ArrowSwitcher;
